import { LOCAL_MIGRATIONS_SQL } from './deviceSchema';
import type { SqlDriver, SqlQuery } from './driver';

export interface MigrationFile {
  name: string;
  sql: string;
}

// drizzle-kit output from packages/db (path is relative to this file: apps/web/src/data → repo root).
const files = import.meta.glob('../../../../packages/db/migrations/*.sql', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

export const DB_MIGRATIONS: MigrationFile[] = Object.entries(files)
  .map(([path, sql]) => ({
    name: path.slice(path.lastIndexOf('/') + 1).replace(/\.sql$/, ''),
    sql,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export function splitStatements(sql: string): string[] {
  return sql
    .split('--> statement-breakpoint')
    .map((s) => s.trim())
    .filter(Boolean);
}

const run = (sql: string, params: unknown[] = []): SqlQuery => ({ sql, params, method: 'run' });

/** Applies missing migrations in name order, each in its own transaction. Returns the names applied. */
export async function runMigrations(
  driver: SqlDriver,
  migrations: MigrationFile[] = DB_MIGRATIONS,
): Promise<string[]> {
  await driver.query(run(LOCAL_MIGRATIONS_SQL));
  const res = await driver.query({
    sql: 'SELECT name FROM local_migrations',
    params: [],
    method: 'all',
  });
  const applied = new Set((res.rows as unknown[][]).map((r) => String(r[0])));
  const now = new Date().toISOString();
  const done: string[] = [];
  for (const m of migrations) {
    if (applied.has(m.name)) continue;
    await driver.batch([
      ...splitStatements(m.sql).map((s) => run(s)),
      run('INSERT INTO local_migrations (name, applied_at) VALUES (?, ?)', [m.name, now]),
    ]);
    done.push(m.name);
  }
  return done;
}
